import { useState, useEffect } from 'react'
import { Plus, Trash2, Lock, Unlock, Wand2, Scale } from 'lucide-react'

// ── helpers ────────────────────────────────────────────────────────────────
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtMoney = v => (v == null || v === '') ? '' : new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)
const parseMoney = s => {
  if (s == null || s === '') return null
  const clean = String(s).replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.')
  const v = parseFloat(clean)
  return isNaN(v) ? null : v
}
const hojeISO = () => new Date().toISOString().slice(0, 10)
const addDiasISO = (iso, dias) => {
  if (!iso) return ''
  const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + dias)
  return d.toISOString().slice(0, 10)
}
const c2 = n => Math.round((n || 0) * 100) / 100
const floor2 = n => Math.floor((n || 0) * 100) / 100
const diasEntre = (a, b) => {
  if (!a || !b) return null
  return Math.round((new Date(b + 'T12:00:00') - new Date(a + 'T12:00:00')) / 86400000)
}

function MoneyInput({ value, onChange, className = '', disabled = false }) {
  const [raw, setRaw] = useState(value != null ? fmtMoney(value) : '')
  useEffect(() => { setRaw(value != null && value !== '' ? fmtMoney(value) : '') }, [value])
  return (
    <div className={`relative flex items-center ${className}`}>
      <span className="absolute left-2.5 text-gray-400 text-xs pointer-events-none">R$</span>
      <input
        type="text" value={raw} disabled={disabled}
        onChange={e => setRaw(e.target.value)}
        onBlur={() => { const v = parseMoney(raw); onChange(v); setRaw(v != null ? fmtMoney(v) : '') }}
        className="w-full pl-7 pr-2 py-1.5 border border-gray-300 rounded-lg text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
      />
    </div>
  )
}

/**
 * Editor de parcelas reutilizável: gerador (N parcelas, 1ª à vista/em N dias,
 * intervalo, data base, escalonamento) + lista editável com trava por linha e
 * "redistribuir restante". Controlado — recebe `parcelas` e `restante` (valor a
 * distribuir) e devolve mudanças via `onChange`.
 */
export default function ParcelasEditor({ restante, parcelas, onChange, refData, className = '' }) {
  const [genN, setGenN] = useState(12)
  const [genPrimeiraAVista, setGenPrimeiraAVista] = useState(true)
  const [genPrimeiraDias, setGenPrimeiraDias] = useState(30)
  const [genIntervalo, setGenIntervalo] = useState(30)
  const [genDataBase, setGenDataBase] = useState('')
  const [escUsar, setEscUsar] = useState(false)
  const [escQtd, setEscQtd] = useState(3)
  const [escValor, setEscValor] = useState(null)

  const somaParcelas = parcelas.reduce((s, p) => s + (p.valor || 0), 0)
  const diferenca = c2(somaParcelas - restante)
  const balanceado = Math.abs(diferenca) < 0.02

  const upd = (idx, field, val) => onChange(parcelas.map((p, i) => i === idx ? { ...p, [field]: val } : p))
  const toggleTravada = idx => onChange(parcelas.map((p, i) => i === idx ? { ...p, travada: !p.travada } : p))
  const remove = idx => onChange(parcelas.filter((_, i) => i !== idx).map((p, i) => ({ ...p, numero: i + 1 })))
  const limpar = () => onChange([])
  const add = () => {
    const last = parcelas[parcelas.length - 1]
    const proxData = last?.data ? addDiasISO(last.data, Number(genIntervalo) || 30) : ''
    onChange([...parcelas, { numero: parcelas.length + 1, valor: null, data: proxData, travada: false }])
  }

  const gerar = () => {
    const n = Math.max(1, Math.min(120, parseInt(genN) || 1))
    if (restante <= 0.01) return
    const base = genDataBase || hojeISO()
    const intervalo = Number(genIntervalo) || 30
    const off0 = genPrimeiraAVista ? 0 : (parseInt(genPrimeiraDias) || 0)
    const escN = escUsar && escValor > 0 ? Math.min(parseInt(escQtd) || 0, n) : 0

    let valores
    if (escN > 0) {
      const nResto = n - escN
      valores = nResto <= 0
        ? Array.from({ length: n }, () => c2(escValor))
        : Array.from({ length: n }, (_, i) => (i < escN ? c2(escValor) : floor2((restante - escN * escValor) / nResto)))
    } else {
      const v = floor2(restante / n)
      valores = Array.from({ length: n }, () => v)
    }
    const soma = c2(valores.reduce((s, x) => s + x, 0))
    valores[n - 1] = c2(valores[n - 1] + (restante - soma))

    onChange(valores.map((valor, i) => ({
      numero: i + 1, valor, data: addDiasISO(base, off0 + intervalo * i), travada: false,
    })))
  }

  const redistribuir = () => {
    const somaTravadas = parcelas.filter(p => p.travada).reduce((s, p) => s + (p.valor || 0), 0)
    const alvo = c2(restante - somaTravadas)
    const livres = parcelas.filter(p => !p.travada).length
    if (livres === 0) return
    const v = floor2(alvo / livres)
    let vistos = 0
    onChange(parcelas.map(p => {
      if (p.travada) return p
      vistos += 1
      return { ...p, valor: vistos === livres ? c2(alvo - v * (livres - 1)) : v }
    }))
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Gerador */}
      <div className="bg-white border border-blue-100 rounded-xl p-3.5 space-y-3">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700">
          <Wand2 size={13} /> Gerar parcelas
        </div>

        <div className="flex flex-wrap items-end gap-x-4 gap-y-2.5">
          <div className="space-y-1">
            <label className="block text-[11px] text-gray-400 font-medium">Nº de parcelas</label>
            <input type="number" min="1" max="120" value={genN}
              onChange={e => setGenN(e.target.value)}
              className="w-20 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="space-y-1">
            <label className="block text-[11px] text-gray-400 font-medium">1ª parcela</label>
            <div className="flex items-center gap-1.5">
              <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
                <button type="button" onClick={() => setGenPrimeiraAVista(true)}
                  className={`px-2.5 py-1.5 font-medium transition-colors ${genPrimeiraAVista ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>À vista</button>
                <button type="button" onClick={() => setGenPrimeiraAVista(false)}
                  className={`px-2.5 py-1.5 font-medium border-l border-gray-300 transition-colors ${!genPrimeiraAVista ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>Em dias</button>
              </div>
              {!genPrimeiraAVista && (
                <input type="number" min="1" value={genPrimeiraDias}
                  onChange={e => setGenPrimeiraDias(e.target.value)}
                  className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500" />
              )}
            </div>
          </div>
          <div className="space-y-1">
            <label className="block text-[11px] text-gray-400 font-medium">Intervalo</label>
            <select value={genIntervalo} onChange={e => setGenIntervalo(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              {[7, 15, 30, 45, 60, 90].map(d => <option key={d} value={d}>{d} dias</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="block text-[11px] text-gray-400 font-medium">Data base</label>
            <input type="date" value={genDataBase} onChange={e => setGenDataBase(e.target.value)}
              className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>

        <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer select-none">
          <input type="checkbox" checked={escUsar} onChange={e => setEscUsar(e.target.checked)}
            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          <span className="flex flex-wrap items-center gap-1.5 leading-6">
            As primeiras
            <input type="number" min="1" value={escQtd} disabled={!escUsar}
              onChange={e => setEscQtd(e.target.value)}
              className="w-14 border border-gray-300 rounded-md px-1.5 py-0.5 text-sm text-center tabular-nums disabled:bg-gray-100 disabled:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            parcela(s) de
            <span className={escUsar ? '' : 'opacity-40 pointer-events-none'}>
              <MoneyInput value={escValor} onChange={setEscValor} className="w-32 inline-block" />
            </span>
            <span className="text-gray-400">— o restante é dividido igualmente.</span>
          </span>
        </label>

        <div className="flex items-center justify-end pt-0.5">
          <button type="button" onClick={gerar} disabled={restante <= 0.01}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-lg transition-colors">
            <Wand2 size={13} /> Gerar {parseInt(genN) || ''} parcelas
          </button>
        </div>
      </div>

      {/* Lista */}
      {parcelas.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-xs text-gray-500 tabular-nums">
            {parcelas.length} parcela{parcelas.length > 1 ? 's' : ''}
            <span className={`ml-2 font-semibold ${balanceado ? 'text-green-600' : 'text-orange-500'}`}>
              {balanceado ? 'fecha o total' : `${diferenca > 0 ? '+' : ''}${brl(diferenca)}`}
            </span>
          </span>
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={redistribuir}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg transition-colors">
              <Scale size={12} /> Redistribuir restante
            </button>
            <button type="button" onClick={add}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-white hover:bg-gray-50 text-gray-600 border border-gray-300 rounded-lg transition-colors">
              <Plus size={12} /> Parcela
            </button>
            <button type="button" onClick={limpar} title="Limpar todas"
              className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      )}

      {parcelas.length === 0 && (
        <div className="text-xs text-gray-400 italic py-1 pl-1">Nenhuma parcela — use o gerador acima.</div>
      )}

      <div className="space-y-1.5">
        {parcelas.map((p, idx) => {
          const dias = diasEntre(refData || genDataBase, p.data)
          return (
            <div key={idx} className={`grid grid-cols-[2.5rem_auto_1fr_1fr_auto] items-center gap-2 rounded-lg border px-2.5 py-1.5 transition-colors ${
              p.travada ? 'bg-blue-50/60 border-blue-200' : 'bg-white border-gray-200'
            }`}>
              <span className="text-[11px] text-gray-400 font-semibold tabular-nums text-center">{p.numero}/{parcelas.length}</span>
              <button type="button" onClick={() => toggleTravada(idx)}
                title={p.travada ? 'Travada — não muda ao redistribuir' : 'Livre — recebe o rateio ao redistribuir'}
                className={`p-1.5 rounded-lg transition-colors ${p.travada ? 'text-blue-600 bg-blue-100' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'}`}>
                {p.travada ? <Lock size={13} /> : <Unlock size={13} />}
              </button>
              <MoneyInput value={p.valor} onChange={val => upd(idx, 'valor', val)} />
              <div className="flex items-center gap-1.5">
                <input type="date" value={p.data || ''} onChange={e => upd(idx, 'data', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                {dias != null && dias >= 0 && <span className="text-[10px] text-gray-400 whitespace-nowrap tabular-nums">{dias}d</span>}
              </div>
              <button type="button" onClick={() => remove(idx)}
                className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                <Trash2 size={13} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
